import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  useDeleteItem,
  useItem,
  useItemHistory,
  useJobs,
  useRefreshItem,
  useUpdateItem,
} from "../api/items.js";
import { BackButton } from "../components/BackButton.js";
import { HistoryChart } from "../components/Chart.js";
import { ErrorState, Loading } from "../components/Loading.js";
import { CronPicker } from "../components/CronPicker.js";
import { BackfillForm } from "../components/BackfillForm.js";
import { formatCount, formatRelative, kindLabel, skinFilterLabel } from "../lib/format.js";

type Range = "1d" | "7d" | "30d" | "all";

const RANGE_OPTS: Range[] = ["1d", "7d", "30d", "all"];

function rangeToFrom(range: Range): string | undefined {
  const now = Date.now();
  switch (range) {
    case "1d":
      return new Date(now - 24 * 3600 * 1000).toISOString();
    case "7d":
      return new Date(now - 7 * 24 * 3600 * 1000).toISOString();
    case "30d":
      return new Date(now - 30 * 24 * 3600 * 1000).toISOString();
    case "all":
      return undefined;
  }
}

export function DetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [range, setRange] = useState<Range>("30d");

  // Для стикеров — селектор «множитель»: null = sum (total), 1..5 = серия по копиям.
  const [multiplicity, setMultiplicity] = useState<1 | 2 | 3 | 4 | 5 | null>(null);

  const itemQ = useItem(id);
  const fromIso = rangeToFrom(range);
  const historyQ = useItemHistory(id, {
    granularity: range === "1d" ? "hour" : "day",
    ...(fromIso ? { from: fromIso } : {}),
    ...(multiplicity ? { multiplicity } : {}),
  });
  const jobsQ = useJobs(id);
  const refresh = useRefreshItem();
  const del = useDeleteItem();
  const update = useUpdateItem(id ?? "");
  const [editingCron, setEditingCron] = useState(false);

  if (!id) return null;
  if (itemQ.isLoading) return <Loading />;
  if (itemQ.isError) return <ErrorState error={itemQ.error} />;
  if (!itemQ.data) return null;
  const item = itemQ.data.item;
  const effectiveCron = itemQ.data.effectiveCron;

  const handleDelete = () => {
    if (!confirm(`Удалить "${item.displayName}"?`)) return;
    del.mutate(item.id, {
      onSuccess: () => navigate("/"),
      // alert как самый честный feedback в локальной разработке без toast-системы
      onError: (err) => alert(`Ошибка удаления: ${(err as Error).message}`),
    });
  };

  return (
    <div className="space-y-4 p-3">
      <BackButton to="/" />
      <div className="rounded-2xl bg-bg-secondary p-4">
        <div className="flex items-center gap-2 text-xs uppercase tracking-wide text-hint">
          <span>{kindLabel(item.kind)}</span>
          {skinFilterLabel(item.filter) && (
            <span className="rounded-md bg-bg px-1.5 py-0.5 normal-case tracking-normal text-text">
              {skinFilterLabel(item.filter)}
            </span>
          )}
        </div>
        <h1 className="mt-1 text-xl font-bold">{item.displayName}</h1>
        <div className="mt-3 flex items-end justify-between">
          <div>
            <div className="text-3xl font-bold tabular-nums">
              {formatCount(item.lastCount)}
            </div>
            <div className="text-xs text-hint">items</div>
          </div>
          <div className="text-right text-xs text-hint">
            обновлено {formatRelative(item.lastSnapshotAt)}
          </div>
        </div>
      </div>

      {/* Расписание */}
      <div className="rounded-xl bg-bg-secondary p-3">
        <div className="flex items-center justify-between">
          <div className="min-w-0 flex-1">
            <div className="text-xs uppercase tracking-wide text-hint">Расписание</div>
            <div className="mt-0.5 flex items-center gap-2 text-sm">
              <code className="rounded bg-bg px-1.5 py-0.5 text-xs">
                {effectiveCron.cron}
              </code>
              <span className="text-xs text-hint">{cronSourceLabel(effectiveCron)}</span>
            </div>
            {effectiveCron.source === "conflict" && (
              <div className="mt-1 rounded-lg bg-destructive/15 p-2 text-xs text-destructive">
                ⚠ Конфликт тегов: {effectiveCron.conflictingTags?.join(", ")}. Item
                использует глобальный default. Установи явное расписание ниже.
              </div>
            )}
          </div>
          <button
            type="button"
            onClick={() => setEditingCron((v) => !v)}
            className="ml-2 shrink-0 text-sm text-link"
          >
            {editingCron ? "Скрыть" : "Изменить"}
          </button>
        </div>
        {editingCron && (
          <div className="mt-3">
            <CronPicker
              value={item.scheduleCron}
              onChange={(cron) => {
                update.mutate(
                  { scheduleCron: cron },
                  {
                    onSuccess: () => setEditingCron(false),
                    onError: (err) => alert(`Ошибка: ${(err as Error).message}`),
                  },
                );
              }}
            />
          </div>
        )}
      </div>

      {/* Теги */}
      <TagsEditor
        tags={item.tags}
        onSave={(tags) =>
          update.mutate(
            { tags },
            { onError: (err) => alert(`Ошибка: ${(err as Error).message}`) },
          )
        }
        saving={update.isPending}
      />

      {/* Multiplicity breakdown (только для стикеров с заполненной разбивкой) */}
      {item.kind === "sticker" && item.lastCountByMultiplicity && (
        <MultiplicityBreakdown
          counts={item.lastCountByMultiplicity}
          selected={multiplicity}
          onSelect={setMultiplicity}
        />
      )}

      {/* Skin trade-up/unboxed (только для скинов с заполненной разбивкой) */}
      {item.kind === "skin" && item.skinOriginBreakdown && (
        <SkinOriginBreakdown breakdown={item.skinOriginBreakdown} />
      )}

      {/* Диапазоны */}
      <div className="flex gap-1 rounded-xl bg-bg-secondary p-1">
        {RANGE_OPTS.map((r) => (
          <button
            key={r}
            type="button"
            onClick={() => setRange(r)}
            className={`flex-1 rounded-lg px-3 py-1.5 text-sm transition ${
              range === r ? "bg-bg font-medium" : "text-hint"
            }`}
          >
            {r}
          </button>
        ))}
      </div>

      {historyQ.isLoading && <Loading label="График..." />}
      {historyQ.isError && <ErrorState error={historyQ.error} />}
      {historyQ.isSuccess && historyQ.data.length > 0 && (
        <div className="rounded-2xl bg-bg-secondary p-3">
          <HistoryChart points={historyQ.data} />
        </div>
      )}
      {historyQ.isSuccess && historyQ.data.length === 0 && (
        <div className="rounded-lg bg-bg-secondary p-4 text-center text-sm text-hint">
          Нет данных за выбранный период
        </div>
      )}

      {/* Действия */}
      <div className="flex gap-2">
        <button
          type="button"
          disabled={refresh.isPending}
          onClick={() =>
            refresh.mutate(item.id, {
              onError: (err) => alert(`Ошибка refresh: ${(err as Error).message}`),
            })
          }
          className="flex-1 rounded-xl bg-button px-4 py-3 font-medium text-button-text disabled:opacity-50"
        >
          {refresh.isPending ? "..." : "🔄 Refresh"}
        </button>
        <button
          type="button"
          disabled={del.isPending}
          onClick={handleDelete}
          className="rounded-xl bg-bg-secondary px-4 py-3 font-medium text-destructive disabled:opacity-50"
        >
          {del.isPending ? "..." : "🗑"}
        </button>
      </div>

      <BackfillForm itemId={item.id} />

      {/* Последние jobs */}
      {jobsQ.data && jobsQ.data.length > 0 && (
        <div className="space-y-1">
          <div className="text-sm font-semibold">Последние запуски</div>
          {jobsQ.data.slice(0, 5).map((j) => (
            <div
              key={j.id}
              className="flex items-center justify-between rounded-lg bg-bg-secondary px-3 py-2 text-xs"
            >
              <span>
                {j.status === "success" ? "✅" : j.status === "failed" ? "❌" : "⏳"} {j.status}
              </span>
              <span className="text-hint">{formatRelative(j.createdAt)}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// Компактный редактор тегов: список chip'ов + поле «добавить новый».
// Save фиксирует draft через переданный onSave (Detail вызывает update.mutate).
function TagsEditor({
  tags,
  onSave,
  saving,
}: {
  tags: string[];
  onSave: (next: string[]) => void;
  saving: boolean;
}) {
  const [draft, setDraft] = useState<string[]>(tags);
  const [input, setInput] = useState("");

  // Синхронизируем draft если родитель прислал новые tags (после успешного PATCH).
  // Ключ — стабильное строковое представление массива.
  const tagsKey = tags.join("|");
  useEffect(() => {
    setDraft(tags);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tagsKey]);

  const addTag = () => {
    const v = input.trim();
    if (!v) return;
    if (draft.includes(v)) {
      setInput("");
      return;
    }
    setDraft([...draft, v]);
    setInput("");
  };

  const removeTag = (t: string) => setDraft(draft.filter((x) => x !== t));
  const dirty = draft.join("") !== tags.join("");

  return (
    <div className="rounded-xl bg-bg-secondary p-3">
      <div className="text-xs uppercase tracking-wide text-hint">Теги</div>
      <div className="mt-2 flex flex-wrap gap-1.5">
        {draft.map((t) => (
          <span
            key={t}
            className="inline-flex items-center gap-1 rounded-full bg-bg px-2 py-1 text-xs"
          >
            {t}
            <button
              type="button"
              onClick={() => removeTag(t)}
              aria-label={`Удалить ${t}`}
              className="text-hint hover:text-destructive"
            >
              ×
            </button>
          </span>
        ))}
        {draft.length === 0 && (
          <span className="text-xs text-hint">нет тегов</span>
        )}
      </div>
      <div className="mt-3 flex gap-2">
        <input
          type="text"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              addTag();
            }
          }}
          placeholder="Новый тег (Enter)"
          className="flex-1 rounded-lg bg-bg px-3 py-1.5 text-sm outline-none placeholder:text-hint"
        />
        <button
          type="button"
          onClick={addTag}
          disabled={!input.trim()}
          className="rounded-lg bg-bg px-3 py-1.5 text-sm disabled:opacity-50"
        >
          +
        </button>
      </div>
      {dirty && (
        <button
          type="button"
          disabled={saving}
          onClick={() => onSave(draft)}
          className="mt-2 w-full rounded-xl bg-button py-2 text-sm font-medium text-button-text disabled:opacity-50"
        >
          {saving ? "..." : "Сохранить теги"}
        </button>
      )}
    </div>
  );
}

// Разбивка стикера по числу копий + селектор серии для графика ниже.
function MultiplicityBreakdown({
  counts,
  selected,
  onSelect,
}: {
  counts: { x1: number; x2: number; x3: number; x4: number; x5: number };
  selected: 1 | 2 | 3 | 4 | 5 | null;
  onSelect: (m: 1 | 2 | 3 | 4 | 5 | null) => void;
}) {
  const cells: Array<{ key: 1 | 2 | 3 | 4 | 5; value: number }> = [
    { key: 1, value: counts.x1 },
    { key: 2, value: counts.x2 },
    { key: 3, value: counts.x3 },
    { key: 4, value: counts.x4 },
    { key: 5, value: counts.x5 },
  ];
  return (
    <div className="rounded-xl bg-bg-secondary p-3">
      <div className="mb-2 text-xs uppercase tracking-wide text-hint">
        Разбивка по числу копий
      </div>
      <div className="grid grid-cols-5 gap-1.5">
        {cells.map((c) => {
          const active = selected === c.key;
          return (
            <button
              key={c.key}
              type="button"
              onClick={() => onSelect(active ? null : c.key)}
              className={`rounded-lg px-2 py-1.5 text-center transition ${
                active ? "bg-button text-button-text" : "bg-bg"
              }`}
            >
              <div className="text-[10px] uppercase tracking-wide opacity-80">×{c.key}</div>
              <div className="text-sm font-medium tabular-nums">{c.value}</div>
            </button>
          );
        })}
      </div>
      <div className="mt-2 text-[11px] text-hint">
        {selected
          ? `График показывает серию с ${selected} копиями`
          : "Клик на ячейку → график переключится на эту серию (сейчас: всего)"}
      </div>
    </div>
  );
}

// Разбивка скина по Steam item origin (через GC inspect): сколько скрафчено в
// trade-up контракте (origin 4), сколько выбито из кейса (origin 8), остальное.
// Значения экстраполированы на полный supply из выборки inspected. Подпись
// честно говорит, что это оценка по выборке.
function SkinOriginBreakdown({
  breakdown,
}: {
  breakdown: { tradeUp: number; unboxed: number; other: number; inspected: number; total: number };
}) {
  const denom = breakdown.total > 0 ? breakdown.total : 1;
  const pct = (n: number) => Math.round((n / denom) * 100);
  const cells: Array<{ label: string; value: number }> = [
    { label: "Trade-Up", value: breakdown.tradeUp },
    { label: "Выбито", value: breakdown.unboxed },
    { label: "Прочее", value: breakdown.other },
  ];
  return (
    <div className="rounded-xl bg-bg-secondary p-3">
      <div className="mb-2 text-xs uppercase tracking-wide text-hint">
        Происхождение (оценка)
      </div>
      <div className="grid grid-cols-3 gap-1.5">
        {cells.map((c) => (
          <div key={c.label} className="rounded-lg bg-bg px-2 py-2">
            <div className="text-[10px] uppercase tracking-wide text-hint">{c.label}</div>
            <div className="text-sm font-medium tabular-nums">
              {formatCount(c.value)} <span className="text-hint">({pct(c.value)}%)</span>
            </div>
          </div>
        ))}
      </div>
      <div className="mt-2 text-[11px] text-hint">
        оценка по выборке {breakdown.inspected} из {formatCount(breakdown.total)} · origin через Steam inspect
      </div>
    </div>
  );
}

// Подпись «откуда взят эффективный cron» для UI блока Расписание.
function cronSourceLabel(ec: {
  source: "override" | "tag" | "default" | "conflict";
  tag?: string;
}): string {
  switch (ec.source) {
    case "override":
      return "явное расписание";
    case "tag":
      return `по тегу: ${ec.tag}`;
    case "default":
      return "по умолчанию (глобальное)";
    case "conflict":
      return "конфликт тегов → default";
  }
}
